// shop-frontend/src/pages/ShopAuth.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { useShopAuth } from "../context/ShopAuthContext";
import { request } from "../utils/api";
import { sendVerificationCode, verifyCode, cleanupFirebaseAuth } from "../utils/firebasePhoneAuth";
import "./ShopAuth.css";

function useQuery() {
  const [searchParams] = useSearchParams();
  return useMemo(() => searchParams, [searchParams]);
}

function normIndustries(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.industries)) return payload.industries;
  if (payload.data && Array.isArray(payload.data.industries)) return payload.data.industries;
  return [];
}

// Removed unused helper functions getIndustryKey and getIndustryName

export default function ShopAuth() {
  const { login } = useShopAuth();
  const nav = useNavigate();
  const query = useQuery();
  const { t } = useTranslation();
  const s = (k, fallback) => t(`shop.${k}`, { defaultValue: fallback });

  const [showLanding, setShowLanding] = useState(true);  // Show landing by default
  const [isLogin, setIsLogin] = useState(true);  // Default to login when viewing auth
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [langOpen, setLangOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(() => {
    try { return localStorage.getItem("shopTheme") || localStorage.getItem("theme") || "dark"; } catch { return "dark"; }
  });

  // Login form
  const [shopEmail, setShopEmail] = useState("");
  const [password, setPassword] = useState("");

  // Register form
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [shopName, setShopName] = useState("");
  const [ownerFirstName, setOwnerFirstName] = useState("");
  const [ownerLastName, setOwnerLastName] = useState("");
  const [ownerDOB, setOwnerDOB] = useState("");
  const [country, setCountry] = useState("");
  const [phoneNumber, setPhoneNumber] = useState(""); // ✅ NEW REQUIRED FIELD
  // eslint-disable-next-line no-unused-vars
  const [selectedIndustry, setSelectedIndustry] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [industries, setIndustries] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [industriesLoading, setIndustriesLoading] = useState(true);
  const [inviterCode, setInviterCode] = useState(""); // ✅ From URL query param
  const [agreedToTerms, setAgreedToTerms] = useState(false); // ✅ Merchant Agreement acceptance

  // OTP verification state
  const [otpStep, setOtpStep] = useState(false);   // true = show OTP input screen
  const [otpCode, setOtpCode] = useState("");
  const [otpSending, setOtpSending] = useState(false);

  const LANGS = [
    { code: "en", name: "English" }, { code: "ar", name: "العربية" }, { code: "am", name: "አማርኛ" },
    { code: "fil", name: "Filipino" }, { code: "fr", name: "Français" }, { code: "de", name: "Deutsch" },
    { code: "hi", name: "हिन्दी" }, { code: "id", name: "Bahasa" }, { code: "it", name: "Italiano" },
    { code: "ja", name: "日本語" }, { code: "ko", name: "한국어" }, { code: "zh", name: "中文" },
    { code: "pt", name: "Português" }, { code: "ru", name: "Русский" }, { code: "so", name: "Soomaali" },
    { code: "es", name: "Español" }, { code: "tr", name: "Türkçe" }, { code: "ur", name: "اردو" },
  ];
  const currentLang = LANGS.find(l => l.code === i18n.language) || LANGS[0];

  // Set cross-domain cookie so homepage can read lang/theme on back navigation
  function setCrossDomainPref(key, val) {
    const isLocal = ["localhost","127.0.0.1"].includes(window.location.hostname);
    const domain = isLocal ? "" : "; domain=.moondala.one";
    document.cookie = `${key}=${val}; path=/; max-age=31536000; SameSite=Lax${domain}`;
  }

  function changeLang(code) {
    i18n.changeLanguage(code);
    localStorage.setItem("userLanguage", code);
    setCrossDomainPref("moondala_lang", code);
    setLangOpen(false);
  }

  function toggleTheme() {
    const next = currentTheme === "dark" ? "light" : "dark";
    setCurrentTheme(next);
    localStorage.setItem("shopTheme", next);
    localStorage.setItem("theme", next);
    setCrossDomainPref("moondala_theme", next);
    const html = document.documentElement;
    html.setAttribute("data-theme", next);
    document.body.className = next === "light" ? "light-theme" : "dark-theme";
  }

  // ✅ Apply language and theme from URL params (passed from user-frontend)
  useEffect(() => {
    const langFromUrl = query.get("lang");
    const themeFromUrl = query.get("theme");
    if (langFromUrl) {
      localStorage.setItem("userLanguage", langFromUrl);
      i18n.changeLanguage(langFromUrl);
    }
    if (themeFromUrl) {
      localStorage.setItem("shopTheme", themeFromUrl);
      localStorage.setItem("theme", themeFromUrl);
      const html = document.documentElement;
      html.setAttribute("data-theme", themeFromUrl);
      document.body.className = themeFromUrl === "light" ? "light-theme" : "dark-theme";
    }
  }, [query]);

  // Load industries on mount and capture inviter code
  useEffect(() => {
    loadIndustries();
    
    // ✅ Capture inviter code from URL query param
    const codeFromUrl = query.get("inviterCode") || query.get("code") || query.get("ref") || "";
    if (codeFromUrl) {
      setInviterCode(codeFromUrl.toUpperCase());
    }
  }, [query]);

  // If already logged in, redirect
  useEffect(() => {
    // Cross-domain login arrives as ?code= and is finished by TokenHandler.
    // 🔒 A ?token= in the URL is ignored (it let anyone log you into their shop).
    if (query.get("code")) return;

    const tok = localStorage.getItem("shopToken") || "";
    const shopStr = localStorage.getItem("shop") || "";
    if (tok && shopStr) {
      // Already logged in, redirect to feed
      nav("/shop/feed", { replace: true });
    }
  }, [nav, query]);

  async function loadIndustries() {
    try {
      setIndustriesLoading(true);
      const res = await request("/api/industries", {
        method: "GET",
      });
      const data = normIndustries(res);
      setIndustries(data);
    } catch (e) {
      console.error("Failed to load industries:", e);
    } finally {
      setIndustriesLoading(false);
    }
  }

  async function handleLogin(e) {
    e.preventDefault();
    setErr("");

    const em = shopEmail.trim().toLowerCase();
    if (!em) return setErr("Please enter your shop email.");
    if (!password) return setErr("Please enter your password.");

    setLoading(true);
    try {
      const res = await login({ shopEmail: em, password });

      // cleanup older sessions
      localStorage.removeItem("token");
      localStorage.removeItem("userToken");
      localStorage.removeItem("user");
      localStorage.removeItem("role");
      localStorage.removeItem("adminToken");
      localStorage.removeItem("admin");

      // verify token exists
      const tokNow = localStorage.getItem("shopToken") || res?.token;
      if (!tokNow) {
        throw new Error("Login response did not save shopToken.");
      }

      // Redirect to shop feed
      nav("/shop/feed", { replace: true });
    } catch (e2) {
      const msg =
        e2?.message ||
        e2?.body?.message ||
        e2?.response?.data?.message ||
        "Login failed";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  }

  // Firebase SMS billing isn't enabled yet — phone verification is switched off
  // until it is (same as the user app). Flip back to true to restore the OTP step.
  const PHONE_VERIFICATION_ENABLED = false;

  // Phase 1: validate form fields then send OTP via Firebase
  async function handleSendOtp(e) {
    e.preventDefault();
    setErr("");

    const em = registerEmail.trim().toLowerCase();
    if (!em) return setErr("Please enter your email.");
    if (!registerPassword) return setErr("Please enter your password.");
    if (!shopName.trim()) return setErr("Please enter your shop name.");
    if (!ownerFirstName.trim()) return setErr("Please enter owner first name.");
    if (!ownerLastName.trim()) return setErr("Please enter owner last name.");
    if (!ownerDOB) return setErr("Please enter date of birth.");
    if (!country.trim()) return setErr("Please select a country.");
    if (!phoneNumber.trim()) return setErr("Please enter phone number.");
    if (!agreedToTerms) return setErr("Please read and accept the Merchant Agreement to continue.");

    // Verification off: register straight away (no SMS code).
    if (!PHONE_VERIFICATION_ENABLED) return completeRegistration("");

    setOtpSending(true);
    try {
      await sendVerificationCode(phoneNumber.trim());
      setOtpStep(true);
      setOtpCode("");
    } catch (e2) {
      setErr(e2?.message || "Failed to send verification code. Check your phone number.");
    } finally {
      setOtpSending(false);
    }
  }

  // Phase 2: verify OTP via Firebase then complete registration
  async function handleRegister(e) {
    e.preventDefault();
    setErr("");

    if (!otpCode.trim() || otpCode.trim().length !== 6) {
      return setErr("Please enter the 6-digit code sent to your phone.");
    }

    let firebaseToken = "";
    try {
      // Verify code with Firebase — get ID token for backend
      const result = await verifyCode(otpCode.trim());
      firebaseToken = await result.user.getIdToken();
      cleanupFirebaseAuth();
    } catch (e2) {
      return setErr(e2?.message || "Wrong or expired code. Please try again.");
    }
    return completeRegistration(firebaseToken);
  }

  // Phase 3: create the shop (firebaseToken is empty while verification is off)
  async function completeRegistration(firebaseToken) {
    setLoading(true);
    try {
      const em = registerEmail.trim().toLowerCase();
      const payload = {
        shopEmail: em,
        password: registerPassword,
        shopName: shopName.trim(),
        ownerFirstName: ownerFirstName.trim(),
        ownerLastName: ownerLastName.trim(),
        ownerDOB,
        country: country.trim(),
        phoneNumber: phoneNumber.trim(),
        ...(firebaseToken ? { firebaseToken } : {}), // backend verifies this to confirm phone
        acceptedMerchantAgreement: true, // ✅ records agent-of-payee / terms acceptance
        merchantAgreementAcceptedAt: new Date().toISOString(),
      };

      if (selectedIndustry) payload.industryId = selectedIndustry;
      if (inviterCode) payload.inviterCode = inviterCode;

      const res = await request("/api/shop/auth/register", {
        method: "POST",
        body: payload,
      });

      if (res.token) {
        localStorage.setItem("shopToken", res.token);
        localStorage.setItem("shop", JSON.stringify(res.shop));
      }

      nav("/shop/feed", { replace: true });

    } catch (e2) {
      const msg =
        e2?.message ||
        e2?.body?.message ||
        e2?.response?.data?.message ||
        "Registration failed";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  }

  const features = [
    { icon: "🔄", title: s("feat1", "Social Commerce Built In"), desc: s("feat1Desc", "Your shop lives inside a social feed. Customers like, comment, share, and buy — all without leaving the app.") },
    { icon: "🎯", title: s("feat2", "Smart Product Matching"), desc: s("feat2Desc", "Your products appear in users' feeds and mall based on what they actually care about. No paying for ads.") },
    { icon: "🏪", title: s("feat3", "Your Own Mall Page"), desc: s("feat3Desc", "Get a custom, branded storefront — not just a product listing buried in search results.") },
    { icon: "📱", title: s("feat4", "Mobile-First, App-Ready"), desc: s("feat4Desc", "Your customers shop from our iOS & Android app. Push notifications, instant checkout, real mobile experience.") },
    { icon: "🚫", title: s("feat5", "No Monthly Fees. Ever."), desc: s("feat5Desc", "No subscription tiers, no hidden costs. We take a small cut when you sell. That's it.") },
    { icon: "⚡", title: s("feat6", "5-Minute Setup"), desc: s("feat6Desc", "Register, add products, start selling. No approval queues, no waiting weeks.") },
  ];

  const compareRows = [
    [s("cmpMonthly", "Monthly Fee"), s("cmpFree", "Free"), "$0–$40+", "$39+/mo"],
    [s("cmpListing", "Listing Fee"), s("cmpFree", "Free"), "$0.20/item", s("cmpFree", "Free")],
    [s("cmpSocial", "Built-in Social Feed"), "✅", "❌", "❌"],
    [s("cmpMobile", "Mobile App for Buyers"), "✅", "✅", "❌"],
    [s("cmpMatching", "Smart Product Matching"), "✅", s("cmpAlgo", "Algorithm-driven"), "❌"],
    [s("cmpMall", "Custom Mall / Storefront"), "✅", s("cmpBasic", "Basic"), "$$$"],
    [s("cmpAudience", "Built-in Audience"), "✅", "✅", "❌"],
  ];

  const steps = [
    { num: "1", title: s("step1", "Create Your Shop"), desc: s("step1Desc", "Sign up free. Pick a name, add your info. Done in 2 minutes.") },
    { num: "2", title: s("step2", "Add Products"), desc: s("step2Desc", "Upload photos, set prices, write descriptions. Bulk upload if you have many.") },
    { num: "3", title: s("step3", "Get Discovered"), desc: s("step3Desc", "Your products show up in the social feed, mall, and search. Customers come to you.") },
  ];

  return (
    <div className="sa-page">
      {/* ===== TOP BAR: Language + Theme ===== */}
      <div className="sa-topbar">
        <div className="sa-topbar-inner">
          <div className="sa-topbar-brand">🌙 Moondala Shop</div>
          <div className="sa-topbar-controls">
            {/* Theme toggle */}
            <button className="sa-topbar-btn" onClick={toggleTheme} aria-label="Toggle theme">
              {currentTheme === "dark" ? "☀️" : "🌙"}
            </button>
            {/* Language dropdown */}
            <div className="sa-lang-wrap" style={{ position: "relative" }}>
              <button className="sa-topbar-btn sa-lang-trigger" onClick={() => setLangOpen(!langOpen)}>
                🌐 {currentLang.code.toUpperCase()} ▾
              </button>
              {langOpen && (
                <>
                  <div style={{ position: "fixed", inset: 0, zIndex: 9998 }} onClick={() => setLangOpen(false)} />
                  <div className="sa-lang-dd">
                    {LANGS.map(l => (
                      <button
                        key={l.code}
                        className={`sa-lang-opt ${l.code === i18n.language ? "active" : ""}`}
                        onClick={() => changeLang(l.code)}
                      >
                        <span className="sa-lang-code">{l.code.toUpperCase()}</span>
                        <span>{l.name}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {showLanding ? (
        <>
          {/* ===== HERO ===== */}
          <div className="sa-hero">
            <div className="sa-hero-inner">
              <h1>{s("heroTitle", "Sell Differently.")}</h1>
              <p className="sa-hero-sub">{s("heroSub", "Moondala isn't just another marketplace. It's a social marketplace where your customers become your promoters, and every sale can spark the next one.")}</p>
              <p className="sa-hero-note">{s("heroNote", "No monthly fees. No listing fees. You only pay when you make money.")}</p>
              <div className="sa-hero-btns">
                <button className="sa-btn-primary" onClick={() => { setShowLanding(false); setIsLogin(false); }}>
                  {s("openShopFree", "Open Your Shop — Free")}
                </button>
                <button className="sa-btn-secondary" onClick={() => { setShowLanding(false); setIsLogin(true); }}>
                  {s("haveAccount", "I Have an Account")}
                </button>
              </div>
            </div>
          </div>

          {/* ===== ECONOMICS CALLOUT: 6% fee + share with users ===== */}
          <div className="sa-section">
            <div
              style={{
                maxWidth: 880,
                margin: "0 auto",
                padding: "32px 28px",
                borderRadius: 20,
                border: "1px solid rgba(34,211,238,0.35)",
                background: "rgba(34,211,238,0.06)",
                textAlign: "center",
              }}
            >
              <h2 className="sa-section-title" style={{ marginBottom: 12 }}>
                {s("econTitle", "We charge 6% — and give most of it back to your customers")}
              </h2>
              <p className="sa-section-sub" style={{ marginBottom: 24 }}>
                {s(
                  "econSub",
                  "Moondala takes a flat 6% per sale and shares up to 5% of it with the community through the referral network. Your customers earn credit every time their network shops — and spend it right back in your store."
                )}
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 16 }}>
                <div style={{ minWidth: 160, padding: "16px 20px", borderRadius: 14, background: "rgba(255,255,255,0.04)" }}>
                  <div style={{ fontSize: 34, fontWeight: 800, color: "#22d3ee" }}>6%</div>
                  <div style={{ fontSize: 14, opacity: 0.8 }}>{s("econFlat", "Flat fee per sale")}</div>
                </div>
                <div style={{ minWidth: 160, padding: "16px 20px", borderRadius: 14, background: "rgba(255,255,255,0.04)" }}>
                  <div style={{ fontSize: 34, fontWeight: 800, color: "#a78bfa" }}>up to 5%</div>
                  <div style={{ fontSize: 14, opacity: 0.8 }}>{s("econShared", "Shared back with buyers")}</div>
                </div>
                <div style={{ minWidth: 160, padding: "16px 20px", borderRadius: 14, background: "rgba(255,255,255,0.04)" }}>
                  <div style={{ fontSize: 34, fontWeight: 800, color: "#f0789e" }}>$$$</div>
                  <div style={{ fontSize: 14, opacity: 0.8 }}>{s("econSpend", "Credit they spend with you")}</div>
                </div>
              </div>
            </div>
          </div>

          {/* ===== FEATURES ===== */}
          <div className="sa-section">
            <h2 className="sa-section-title">{s("whySellers", "Why Sellers Choose Moondala")}</h2>
            <p className="sa-section-sub">{s("whySellersSub", "We built what we wished existed — a platform that actually helps you grow, not just hosts your products.")}</p>
            <div className="sa-features">
              {features.map((f, i) => (
                <div className="sa-feature" key={i}>
                  <div className="sa-feature-icon">{f.icon}</div>
                  <h3>{f.title}</h3>
                  <p>{f.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ===== COMPARISON ===== */}
          <div className="sa-compare">
            <div className="sa-compare-inner">
              <h2 className="sa-section-title" style={{ marginBottom: 36 }}>{s("vsOthers", "Moondala vs. Others")}</h2>
              <div className="sa-table-wrap">
                <table className="sa-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th className="sa-th-highlight">Moondala</th>
                      <th>Etsy / Amazon</th>
                      <th>Shopify</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compareRows.map((row, i) => (
                      <tr key={i}>
                        <td>{row[0]}</td>
                        <td className="sa-td-highlight">{row[1]}</td>
                        <td>{row[2]}</td>
                        <td>{row[3]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ===== STEPS ===== */}
          <div className="sa-section">
            <h2 className="sa-section-title" style={{ marginBottom: 44 }}>{s("stepsTitle", "Start Selling in 3 Steps")}</h2>
            <div className="sa-steps">
              {steps.map((s, i) => (
                <div className="sa-step" key={i}>
                  <div className="sa-step-num">{s.num}</div>
                  <div>
                    <h3>{s.title}</h3>
                    <p>{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ===== CTA ===== */}
          <div className="sa-cta">
            <h2>{s("ctaTitle", "Your shop is waiting.")}</h2>
            <p>{s("ctaSub", "Free to start. No credit card required. Set up in minutes.")}</p>
            <button className="sa-btn-primary" onClick={() => { setShowLanding(false); setIsLogin(false); }}>
              {s("ctaBtn", "Open Your Shop Now")}
            </button>
          </div>
        </>
      ) : (
        /* ===== AUTH VIEW ===== */
        <div className="sa-auth-wrap">
          <div className="sa-card">
            <div className="sa-card-brand">
              <img src="/logo192.png" alt="Moondala" />
              Moondala Shop
            </div>

            <button className="sa-back-btn" onClick={() => setShowLanding(true)}>
              ← {s("backToOverview", "Back to Overview")}
            </button>

            <div className="sa-tabs">
              <button className={`sa-tab ${isLogin ? "active" : ""}`} onClick={() => setIsLogin(true)}>{s("login", "Login")}</button>
              <button className={`sa-tab ${!isLogin ? "active" : ""}`} onClick={() => setIsLogin(false)}>{s("signup", "Register")}</button>
            </div>

            {err && <div className="sa-err">{err}</div>}

            {isLogin ? (
              <form onSubmit={handleLogin}>
                <div className="sa-form-title">{s("shopLogin", "Shop Login")}</div>

                <label className="sa-label">{s("email", "Email")}</label>
                <input className="sa-input" value={shopEmail} onChange={(e) => setShopEmail(e.target.value)} placeholder="shop@email.com" type="email" autoComplete="email" required />

                <label className="sa-label">{s("password", "Password")}</label>
                <input className="sa-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" required />

                <button type="submit" disabled={loading} className="sa-submit">
                  {loading ? s("loggingIn", "Logging in...") : s("login", "Login")}
                </button>
              </form>
            ) : otpStep ? (
              /* ── OTP VERIFICATION STEP ── */
              <form onSubmit={handleRegister}>
                <div className="sa-form-title">📱 {s("verifyPhone", "Verify Your Phone")}</div>

                <p style={{ color: "var(--text-secondary, #aaa)", fontSize: 14, marginBottom: 20, textAlign: "center" }}>
                  {s("otpSentTo", "We sent a 6-digit code to")} <strong>{phoneNumber}</strong>
                </p>

                <label className="sa-label">{s("otpCode", "Verification Code")}</label>
                <input
                  className="sa-input"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  autoComplete="one-time-code"
                  required
                  style={{ letterSpacing: "0.3em", fontSize: 22, textAlign: "center" }}
                />

                <button type="submit" disabled={loading} className="sa-submit">
                  {loading ? s("verifying", "Verifying...") : s("verifyAndCreate", "Verify & Create Shop")}
                </button>

                <button
                  type="button"
                  className="sa-back-btn"
                  style={{ marginTop: 12, width: "100%" }}
                  disabled={otpSending}
                  onClick={async () => {
                    setErr("");
                    setOtpSending(true);
                    try {
                      await sendVerificationCode(phoneNumber.trim());
                      setOtpCode("");
                    } catch (e2) {
                      setErr(e2?.message || "Failed to resend code");
                    } finally {
                      setOtpSending(false);
                    }
                  }}
                >
                  {otpSending ? s("sending", "Sending...") : s("resendCode", "Resend Code")}
                </button>

                <button
                  type="button"
                  className="sa-back-btn"
                  style={{ marginTop: 8, width: "100%" }}
                  onClick={() => { setOtpStep(false); setErr(""); }}
                >
                  ← {s("backToForm", "Back to Form")}
                </button>
              </form>
            ) : (
              /* ── REGISTRATION FORM ── */
              <form onSubmit={handleSendOtp}>
                <div className="sa-form-title">{s("createShopAccount", "Create Shop Account")}</div>

                {inviterCode && (
                  <div className="sa-inviter-badge" style={{ marginBottom: 16 }}>
                    ✅ Registered via referral: <strong>{inviterCode}</strong>
                  </div>
                )}

                <label className="sa-label">{s("shopEmail", "Shop Email")}</label>
                <input className="sa-input" value={registerEmail} onChange={(e) => setRegisterEmail(e.target.value)} placeholder="shop@email.com" type="email" autoComplete="email" required />

                <label className="sa-label">{s("shopName", "Shop Name")}</label>
                <input className="sa-input" value={shopName} onChange={(e) => setShopName(e.target.value)} placeholder={s("shopNamePlaceholder", "My Awesome Shop")} type="text" required />

                <label className="sa-label">{s("ownerFirstName", "Owner First Name")}</label>
                <input className="sa-input" value={ownerFirstName} onChange={(e) => setOwnerFirstName(e.target.value)} type="text" required />

                <label className="sa-label">{s("ownerLastName", "Owner Last Name")}</label>
                <input className="sa-input" value={ownerLastName} onChange={(e) => setOwnerLastName(e.target.value)} type="text" required />

                <label className="sa-label">{s("dob", "Date of Birth")}</label>
                <input className="sa-input" value={ownerDOB} onChange={(e) => setOwnerDOB(e.target.value)} type="date" required />

                <label className="sa-label">{s("country", "Country")}</label>
                <input className="sa-input" value={country} onChange={(e) => setCountry(e.target.value)} type="text" required />

                <label className="sa-label">{s("phone", "Phone Number")}</label>
                <input className="sa-input" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} placeholder="+966 50 123 4567" type="tel" required />

                <label className="sa-label">{s("password", "Password")}</label>
                <input className="sa-input" type="password" value={registerPassword} onChange={(e) => setRegisterPassword(e.target.value)} placeholder="••••••••" autoComplete="new-password" required />

                {inviterCode && (
                  <div className="sa-inviter-badge">✅ Invited By: <strong>{inviterCode}</strong></div>
                )}

                <label style={{ display: "flex", alignItems: "flex-start", gap: 8, margin: "16px 0", fontSize: 13, lineHeight: 1.45 }}>
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    style={{ marginTop: 3, flexShrink: 0 }}
                  />
                  <span>
                    {s("agreeMerchantPrefix", "I have read and agree to the")}{" "}
                    <a
                      href="/shop/merchant-agreement"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#22d3ee", fontWeight: 700, textDecoration: "underline" }}
                    >
                      {s("merchantAgreement", "Merchant Agreement")} ↗
                    </a>
                    {s("agreeMerchantSuffix", ", including that Moondala collects buyer payments as my limited payment agent.")}
                  </span>
                </label>

                <button type="submit" disabled={otpSending} className="sa-submit">
                  {otpSending || loading
                    ? s("sending", "Please wait...")
                    : PHONE_VERIFICATION_ENABLED
                    ? s("continueVerify", "Continue — Verify Phone")
                    : s("createShop", "Create Shop")}
                </button>

                {/* Invisible reCAPTCHA mount point required by Firebase Phone Auth */}
                <div id="recaptcha-container" />
              </form>
            )}

            <div className="sa-switch">
              {isLogin ? (
                <>{s("noAccount", "Don't have a shop account?")}{" "}<button onClick={() => setIsLogin(false)}>{s("applyHere", "Apply here")}</button></>
              ) : (
                <>{s("hasAccount", "Already have an account?")}{" "}<button onClick={() => setIsLogin(true)}>{s("loginHere", "Login here")}</button></>
              )}
            </div>

            <div className="sa-divider" />
            <button
              className="sa-go-user"
              onClick={() => {
                const userAppUrl =
                  process.env.REACT_APP_USER_APP_URL ||
                  process.env.REACT_APP_USER_FRONTEND_URL ||
                  (typeof window !== "undefined" && ["localhost", "127.0.0.1"].includes(window.location.hostname)
                    ? "http://localhost:5173"
                    : "https://moondala.one");
                const uLang = i18n.language || "en";
                const uTheme = currentTheme || "dark";
                window.location.href = `${userAppUrl}?lang=${uLang}&theme=${uTheme}`;
              }}
            >
              👤 {s("goToUserApp", "Go to User App")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
